import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useLocale } from '@/i18n/LocaleProvider'
import { getOrders, type OrderOut } from '@/lib/api'
import { formatDateTime, formatMoney, formatQty } from '@/lib/format'

const RECON_KEY: Record<string, string> = {
  pending: 'recon.pending',
  matched: 'recon.matched',
  unmatched: 'recon.unmatched',
  partial: 'recon.partial',
  conflict: 'recon.conflict',
  review: 'recon.review',
  reversed: 'recon.reversed',
}
const ORDER_STATUS_KEY: Record<string, string> = {
  created: 'ost.created',
  paid: 'ost.paid',
  released: 'ost.released',
  completed: 'ost.completed',
  cancelled: 'ost.cancelled',
  appeal: 'ost.appeal',
}

const LIFECYCLE_CHIPS = [
  { id: '', key: 'life.all' },
  { id: 'active', key: 'life.active' },
  { id: 'done', key: 'life.done' },
  { id: 'appeal', key: 'life.appeal' },
] as const

export function Orders() {
  const { t } = useLocale()
  const [searchParams, setSearchParams] = useSearchParams()
  const lifecycle = searchParams.get('lifecycle') ?? ''
  const [q, setQ] = useState('')
  const [side, setSide] = useState('')
  const [recon, setRecon] = useState('')
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['orders', q, side, recon, lifecycle],
    queryFn: () =>
      getOrders({
        q: q || undefined,
        side: side || undefined,
        recon_status: recon || undefined,
        lifecycle: lifecycle || undefined,
      }),
  })

  const setLifecycle = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('lifecycle', value)
    else next.delete('lifecycle')
    setSearchParams(next)
  }

  const chipCount = (id: string) => {
    if (!data || !id) return null
    if (id === 'active') return data.active_count ?? 0
    if (id === 'done') return data.done_count ?? 0
    if (id === 'appeal') return data.appeal_count ?? 0
    return null
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">{t('orders.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('orders.lead')}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/import" className="rounded-md border border-surface-border px-3 py-2 text-sm text-slate-300">
            {t('orders.import')}
          </Link>
          <Link to="/orders/new" className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-900">
            {t('orders.new')}
          </Link>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {LIFECYCLE_CHIPS.map((chip) => {
          const on = lifecycle === chip.id
          const count = chipCount(chip.id)
          return (
            <button
              key={chip.id || 'all'}
              type="button"
              className={on ? 'mma-life-chip mma-life-chip-on' : 'mma-life-chip'}
              aria-pressed={on}
              onClick={() => setLifecycle(chip.id)}
            >
              {t(chip.key)}
              {count !== null ? <span className="num">{count}</span> : null}
            </button>
          )
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder={t('orders.search')}
          className="w-64 rounded-md border border-surface-border bg-surface px-3 py-1.5 text-sm"
        />
        <select
          value={side}
          onChange={(event) => setSide(event.target.value)}
          className="rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
        >
          <option value="">{t('orders.allSides')}</option>
          <option value="buy">{t('side.buy')}</option>
          <option value="sell">{t('side.sell')}</option>
        </select>
        <select
          value={recon}
          onChange={(event) => setRecon(event.target.value)}
          className="rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
        >
          <option value="">{t('orders.allRecon')}</option>
          <option value="pending">{t('recon.Pending')}</option>
          <option value="matched">{t('recon.Matched')}</option>
          <option value="unmatched">{t('recon.Unmatched')}</option>
        </select>
      </div>

      {isLoading && <p className="mt-6 text-sm text-slate-500">{t('common.loading')}</p>}
      {isError && <p className="mt-6 text-sm text-loss">{error instanceof Error ? error.message : t('common.error')}</p>}

      {data && data.items.length === 0 && (
        <p className="mt-8 text-sm text-slate-500">{t('orders.empty')}</p>
      )}

      {data && data.items.length > 0 && (
        <>
          <ul className="mt-5 space-y-2 md:hidden">
            {data.items.map((order) => (
              <li key={order.id}>
                <OrderCard order={order} />
              </li>
            ))}
          </ul>
          <div className="mt-5 hidden overflow-x-auto md:block">
            <table className="w-full min-w-[52rem] text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="py-2">{t('orders.time')}</th>
                  <th className="py-2">{t('common.exchange')}</th>
                  <th className="py-2">{t('common.side')}</th>
                  <th className="py-2 text-right">{t('orders.qty')}</th>
                  <th className="py-2 text-right">{t('common.price')}</th>
                  <th className="py-2 text-right">{t('orders.sum')}</th>
                  <th className="py-2">{t('common.bank')}</th>
                  <th className="py-2">{t('common.counterparty')}</th>
                  <th className="py-2">{t('orders.state')}</th>
                  <th className="py-2">{t('orders.recon')}</th>
                  <th className="py-2 text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((order) => (
                  <tr key={order.id} className="border-t border-surface-border/60">
                    <td className="py-2">
                      <Link to={`/orders/${order.id}`} className="text-slate-200 hover:underline">
                        {formatDateTime(order.occurred_at)}
                      </Link>
                    </td>
                    <td className="py-2 text-slate-400">{order.exchange_code}</td>
                    <td className={order.side === 'buy' ? 'text-profit' : 'text-loss'}>{t(order.side === 'buy' ? 'side.buy' : 'side.sell')}</td>
                    <td className="num py-2 text-right">
                      {formatQty(order.quantity)} {order.asset_code}
                    </td>
                    <td className="num py-2 text-right">{formatMoney(order.price, order.fiat_code)}</td>
                    <td className="num py-2 text-right">{formatMoney(order.fiat_amount, order.fiat_code)}</td>
                    <td className="py-2 text-slate-400">{order.bank_name ?? '—'}</td>
                    <td className="py-2">{order.counterparty_nickname ?? '—'}</td>
                    <td className="py-2 text-xs text-slate-300">
                      {ORDER_STATUS_KEY[order.order_status] ? t(ORDER_STATUS_KEY[order.order_status] as string) : order.order_status}
                    </td>
                    <td className="py-2 text-xs text-slate-400">{RECON_KEY[order.recon_status] ? t(RECON_KEY[order.recon_status] as string) : order.recon_status}</td>
                    <td className="py-2 text-right">
                      <EditLink orderId={order.id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {data && <p className="mt-3 text-xs text-slate-600">{t('orders.shown', { shown: data.items.length, total: data.total })}</p>}
    </div>
  )
}

function OrderCard({ order }: { order: OrderOut }) {
  const { t } = useLocale()
  return (
    <Link
      to={`/orders/${order.id}`}
      className="block rounded-lg border border-surface-border bg-surface-raised p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm text-slate-200">{formatDateTime(order.occurred_at)}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {order.exchange_code}
            {order.counterparty_nickname ? ` · ${order.counterparty_nickname}` : ''}
          </div>
        </div>
        <span className="inline-flex min-h-11 shrink-0 items-center rounded-md border border-surface-border px-3 text-xs text-slate-200">
          {t('orders.edit')}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-2">
        <span className={order.side === 'buy' ? 'text-sm text-profit' : 'text-sm text-loss'}>
          {t(order.side === 'buy' ? 'side.buy' : 'side.sell')}
        </span>
        <span className="num text-sm text-slate-100">
          {formatQty(order.quantity)} {order.asset_code}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap justify-between gap-2 text-xs text-slate-500">
        <span className="num">{formatMoney(order.price, order.fiat_code)}</span>
        <span className="num text-slate-300">{formatMoney(order.fiat_amount, order.fiat_code)}</span>
      </div>
    </Link>
  )
}

function EditLink({ orderId }: { orderId: string }) {
  const { t } = useLocale()
  return (
    <Link
      to={`/orders/${orderId}`}
      className="inline-flex min-h-11 items-center rounded-md border border-surface-border px-3 text-xs text-slate-200 hover:border-slate-400 hover:text-white"
    >
      {t('orders.edit')}
    </Link>
  )
}
